import { create } from "zustand";

const CHANNEL_NAME = "somethings-notification-mute";
const TAB_ID = Math.random().toString(36).slice(2);
const HEARTBEAT_MS = 3000;
const STALE_MS = 8000;

type MuteMessage =
  | { type: "presence"; tab: string; active: boolean; ts: number }
  | { type: "query"; tab: string };

interface NotificationMuteStore {
  activeTokens: Set<string>;
  acquire: (token: string) => void;
  release: (token: string) => void;
}

export const useNotificationMuteStore = create<NotificationMuteStore>(
  (set) => ({
    activeTokens: new Set<string>(),
    acquire: (token) => {
      const wasActive = isLocallyActive();
      set((state) => {
        if (state.activeTokens.has(token)) return state;
        const next = new Set(state.activeTokens);
        next.add(token);
        return { activeTokens: next };
      });
      if (!wasActive && isLocallyActive()) broadcastPresence();
    },
    release: (token) => {
      const wasActive = isLocallyActive();
      set((state) => {
        if (!state.activeTokens.has(token)) return state;
        const next = new Set(state.activeTokens);
        next.delete(token);
        return { activeTokens: next };
      });
      if (wasActive && !isLocallyActive()) broadcastPresence();
    },
  }),
);

const isLocallyActive = (): boolean =>
  useNotificationMuteStore.getState().activeTokens.size > 0;

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

const remoteSeen = new Map<string, number>();

function pruneStaleRemotes() {
  const now = Date.now();
  for (const [tab, ts] of remoteSeen) {
    if (now - ts > STALE_MS) remoteSeen.delete(tab);
  }
}

function broadcastPresence() {
  const message: MuteMessage = {
    type: "presence",
    tab: TAB_ID,
    active: isLocallyActive(),
    ts: Date.now(),
  };
  channel?.postMessage(message);
}

if (channel) {
  channel.onmessage = (event) => {
    const msg = event.data as MuteMessage;
    if (!msg || msg.tab === TAB_ID) return;

    if (msg.type === "query") {
      broadcastPresence();
      return;
    }
    if (msg.type === "presence" && msg.tab) {
      if (msg.active) remoteSeen.set(msg.tab, msg.ts ?? Date.now());
      else remoteSeen.delete(msg.tab);
      pruneStaleRemotes();
    }
  };

  const query: MuteMessage = { type: "query", tab: TAB_ID };
  channel.postMessage(query);

  const heartbeat = setInterval(() => {
    if (isLocallyActive()) broadcastPresence();
    pruneStaleRemotes();
  }, HEARTBEAT_MS);

  const announceInactive = () => {
    const message: MuteMessage = {
      type: "presence",
      tab: TAB_ID,
      active: false,
      ts: Date.now(),
    };
    channel.postMessage(message);
  };

  const announceOnVisibility = () => {
    if (isLocallyActive()) broadcastPresence();
  };
  globalThis.addEventListener?.("pagehide", announceInactive);
  globalThis.addEventListener?.("beforeunload", announceInactive);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", announceOnVisibility);
  }

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      clearInterval(heartbeat);
      channel?.close();
      globalThis.removeEventListener?.("pagehide", announceInactive);
      globalThis.removeEventListener?.("beforeunload", announceInactive);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", announceOnVisibility);
      }
    });
  }
}

export const isNotificationMuteActive = (): boolean =>
  isLocallyActive() || remoteSeen.size > 0;
